import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AuthCallback from './pages/AuthCallback';
import { DeviceProvider } from './contexts/DeviceContext';
import { MusicProvider, useMusic } from './contexts/MusicContext';
import { RemoteBusProvider } from './contexts/RemoteBusContext';
import { AiPlayerProvider } from './contexts/AiPlayerContext';
import { Toaster } from './components/ui/sonner';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ClientDashboardPage from './pages/ClientDashboardPage';
import ControlBoothPage from './pages/ControlBoothPage';
import PlaylistsPage from './pages/PlaylistsPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import PlaylistPage from './pages/PlaylistPage';
import AdminClientsPage from './pages/AdminClientsPage';
import AdminPlaylistsPage from './pages/AdminPlaylistsPage';
import AdminAnalyticsPage from './pages/AdminAnalyticsPage';
import VenueLandingPage from './pages/VenueLandingPage';
import SettingsPage from './pages/SettingsPage';
import MenuAdminPage from './pages/MenuAdminPage';
import AdminClientMenuPage from './pages/AdminClientMenuPage';
import AdminClientSchedulePage from './pages/AdminClientSchedulePage';
import AdminAiSchedulePage from './pages/AdminAiSchedulePage';
import AdminOwnersPage from './pages/AdminOwnersPage';
import OwnerDashboardPage from './pages/OwnerDashboardPage';
import GuestRequestPage from './pages/GuestRequestPage';
import GuestShoutoutPage from './pages/GuestShoutoutPage';
import GuestMenuPage from './pages/GuestMenuPage';
import GuestReviewPage from './pages/GuestReviewPage';
import TVDisplayPage from './pages/TVDisplayPage';
import TVThemeRedirect from './pages/TVThemeRedirect';
import ReviewsAdminPage from './pages/ReviewsAdminPage';
import GamesAdminPage from './pages/GamesAdminPage';
import GuestGamesPage from './pages/GuestGamesPage';
import StaffPage from './pages/StaffPage';
import EventsPage from './pages/EventsPage';
import BoothPreview from './pages/BoothPreview';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import LandingPage from './pages/LandingPage';
import AdminForensicsPage from './pages/AdminForensicsPage';
import AdminAILibraryPage from './pages/AdminAILibraryPage';
import AdminCatalogPage from './pages/AdminCatalogPage';
import MarketingSite from './pages/MarketingSite';
import RadioPage from './pages/RadioPage';
import AdminRadioPage from './pages/AdminRadioPage';
import ProducerDashboardPage from './pages/ProducerDashboardPage';
import AiControlBoothPage from './pages/AiControlBoothPage';
import ClientOrdersPage from './pages/ClientOrdersPage';
import GuestsPage from './pages/GuestsPage';
import GuestEventPage from './pages/GuestEventPage';
import SplashScreen from './components/SplashScreen';
import VenueEventsListPage from './pages/VenueEventsListPage';
import { PWAInstallBanner } from './components/PWAInstall';
import { ConnectivityPill } from './components/ConnectivityPill';
import { AdBannerPill } from './components/AdBannerPill';
import { installAxiosDefaults } from './utils/axiosSetup';
import { startMemoryPressureWatch } from './utils/memoryPressure';
import './App.css';

// Global axios config (15 s timeout, credentials, network-status events)
// MUST run before any page-level axios call — this module is imported first.
installAxiosDefaults();

// Memory-pressure watchdog for long-running venue tablets (Chrome-only).
// No-op on Firefox/Safari — `performance.memory` is Chrome-specific.
if (typeof window !== 'undefined') startMemoryPressureWatch();

const ProtectedRoute = ({ children, superAdminOnly = false, ownerOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000000] flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (superAdminOnly && user.role !== 'superadmin') {
    return <Navigate to="/" replace />;
  }

  if (ownerOnly && user.role !== 'owner' && user.role !== 'superadmin') {
    return <Navigate to="/" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000000] flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const HomeRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000000] flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  // Unauthenticated visitors see the public marketing landing page.
  // Google's OAuth verification review team requires the app's home page
  // URL to render publicly, explain the product, and link to the privacy
  // policy — this route serves all three.
  if (!user) {
    return <MarketingSite />;
  }

  if (user.role === 'superadmin') {
    return <Navigate to="/admin" replace />;
  }

  if (user.role === 'producer') {
    return <Navigate to="/producer" replace />;
  }

  if (user.role === 'owner') {
    return <Navigate to="/owner" replace />;
  }

  // AI-type venues get a different booth home (in-house library, crossfader,
  // no YouTube). Commercial venues get the classic Control Booth.
  if (user.client_type === 'ai') {
    return <AiControlBoothPage />;
  }

  // The Control Booth is the new venue home (Feb 2026). The metrics that
  // used to live here now live at /analytics.
  return <ControlBoothPage />;
};

function AppRoutes() {
  const location = useLocation();
  // Detect Emergent OAuth callback: `#session_id=...` in the URL fragment.
  // Route synchronously during render (not useEffect) to avoid a race with
  // AuthProvider's own /me check.
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />

      {/* Legal — public pages linked from the login footer and required
          for Google OAuth verification + YouTube API ToS compliance. */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/radio" element={<RadioPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/refund" element={<RefundPolicyPage />} />
      <Route path="/refund-policy" element={<RefundPolicyPage />} />

      <Route path="/" element={<HomeRedirect />} />

      {/* Analytics — the old venue dashboard metrics moved here Feb 2026 so the
          Home / (Control Booth) can focus on real-time playback control. */}
      <Route path="/analytics" element={
        <ProtectedRoute>
          <ClientDashboardPage />
        </ProtectedRoute>
      } />

      <Route path="/playlist/:id" element={
        <ProtectedRoute>
          <PlaylistPage />
        </ProtectedRoute>
      } />
      <Route path="/playlists" element={
        <ProtectedRoute>
          <PlaylistsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/admin" element={
        <ProtectedRoute superAdminOnly>
          <AdminDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/clients" element={
        <ProtectedRoute superAdminOnly>
          <AdminClientsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/clients/:clientId/menu" element={
        <ProtectedRoute superAdminOnly>
          <AdminClientMenuPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/clients/:clientId/schedule" element={
        <ProtectedRoute superAdminOnly>
          <AdminClientSchedulePage />
        </ProtectedRoute>
      } />
      <Route path="/admin/clients/:clientId/ai-schedule" element={
        <ProtectedRoute superAdminOnly>
          <AdminAiSchedulePage />
        </ProtectedRoute>
      } />
      <Route path="/admin/owners" element={
        <ProtectedRoute superAdminOnly>
          <AdminOwnersPage />
        </ProtectedRoute>
      } />
      <Route path="/owner" element={
        <ProtectedRoute ownerOnly>
          <OwnerDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/playlists" element={
        <ProtectedRoute superAdminOnly>
          <AdminPlaylistsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/analytics" element={
        <ProtectedRoute superAdminOnly>
          <AdminAnalyticsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/forensics" element={
        <ProtectedRoute superAdminOnly>
          <AdminForensicsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/ai-library" element={
        <ProtectedRoute superAdminOnly>
          <AdminAILibraryPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/catalog" element={
        <ProtectedRoute superAdminOnly>
          <AdminCatalogPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/radio" element={
        <ProtectedRoute superAdminOnly>
          <AdminRadioPage />
        </ProtectedRoute>
      } />
      {/* Legacy redirect — old bookmarks to /admin/telemetry land on the new analytics dashboard. */}
      <Route path="/admin/telemetry" element={<Navigate to="/admin/analytics" replace />} />
      <Route path="/admin/requests" element={
        <ProtectedRoute superAdminOnly>
          <SettingsPage />
        </ProtectedRoute>
      } />
      
      {/* Settings (formerly Requests) - accessible by both admin and client */}
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/requests" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />

      {/* /dj-console retired Feb 2026 — page file deleted. Home page now
          owns playback UX. Legacy bookmarks bounce to /. */}
      <Route path="/dj-console" element={<Navigate to="/" replace />} />

      {/* Booth Preview — visual mock for design review, no live data */}
      <Route path="/booth-preview" element={
        <ProtectedRoute>
          <BoothPreview />
        </ProtectedRoute>
      } />

      {/* Guests — verified OTP scans + CSV export */}
      <Route path="/guests" element={
        <ProtectedRoute>
          <GuestsPage />
        </ProtectedRoute>
      } />

      {/* Menu admin (host CRUD) */}
      <Route path="/menu" element={
        <ProtectedRoute>
          <MenuAdminPage />
        </ProtectedRoute>
      } />
      <Route path="/reviews" element={
        <ProtectedRoute>
          <ReviewsAdminPage />
        </ProtectedRoute>
      } />
      <Route path="/games" element={
        <ProtectedRoute>
          <GamesAdminPage />
        </ProtectedRoute>
      } />
      <Route path="/events" element={
        <ProtectedRoute>
          <EventsPage />
        </ProtectedRoute>
      } />
      <Route path="/orders" element={
        <ProtectedRoute>
          <ClientOrdersPage />
        </ProtectedRoute>
      } />
      
      {/* Public guest request page - no auth */}
      <Route path="/request/:code" element={<GuestRequestPage />} />
      <Route path="/v/:venueCode" element={<VenueLandingPage />} />
      <Route path="/v/:venueCode/shoutout" element={<GuestShoutoutPage />} />
      <Route path="/menu/:code" element={<GuestMenuPage />} />
      <Route path="/review/:code" element={<GuestReviewPage />} />
      <Route path="/play/:code" element={<GuestGamesPage />} />
      <Route path="/event/:shareCode" element={<GuestEventPage />} />
      <Route path="/v/:venueCode/events" element={<VenueEventsListPage />} />
      <Route path="/tv/:code" element={<TVDisplayPage />} />
      {/* Permanent TV stream URL — resolved by venue_code, works on any smart TV 24/7.
          Hands off to the chosen vanilla theme renderer under /tv/*.html */}
      <Route path="/:venueCode/tv" element={<TVThemeRedirect />} />
      {/* Dedicated DJ Hand-off mobile mode (PIN auth, scoped UI).
          /dj-handoff/:venueCode is the QR-scan target that pre-fills venue code;
          /staff kept as a legacy fallback for anyone bookmarked to the old URL. */}
      <Route path="/dj-handoff" element={<StaffPage />} />
      <Route path="/dj-handoff/:venueCode" element={<StaffPage />} />
      <Route path="/staff" element={<StaffPage />} />
      <Route path="/producer" element={
        <ProtectedRoute>
          <ProducerDashboardPage />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

// Bridge component — consumes MusicContext and hands a `dispatch` fn to the
// RemoteBus, which invokes it whenever a real-time command arrives over WS.
// Only the commands MusicContext actually exposes today are wired here;
// anything else (set_volume, seek, load_playlist) falls through to the
// existing 1s polling fallback in DJ Console so nothing regresses.
const RemoteBusBridge = ({ children }) => {
  const music = useMusic();
  const dispatch = React.useCallback((command /* , payload */) => {
    switch (command) {
      case 'play':  music.play?.(); break;
      case 'pause': music.pause?.(); break;
      case 'next':  music.nextTrack?.(); break;
      case 'prev':  music.previousTrack?.(); break;
      default: /* handled by REST polling fallback */ break;
    }
  }, [music]);
  return <RemoteBusProvider dispatch={dispatch}>{children}</RemoteBusProvider>;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <DeviceProvider>
          <MusicProvider>
            <RemoteBusBridge>
              <AiPlayerProvider>
                <SplashScreen />
                <AppRoutes />
                <PWAInstallBanner />
                <ConnectivityPill variant="floating" />
                <AdBannerPill />
                <Toaster position="top-right" />
              </AiPlayerProvider>
            </RemoteBusBridge>
          </MusicProvider>
        </DeviceProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
